import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { FileText, BookOpen, Lightbulb, FileQuestion, Edit, Trash2, Search, Plus, Filter, AlertTriangle, Upload, X, Loader2, Image as ImageIcon, RefreshCw, ExternalLink, Video as VideoIcon, PlayCircle, Download, Copy } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { toast } from 'sonner';
import axios from 'axios';
import QuestionModal from './QuestionModal';
import { StagedQuestionsPanel } from './StagedQuestionsPanel';
import { PurgeImageQuestionsBar } from './PurgeImageQuestionsBar';
import LessonModal from './LessonModal';
import VideoModal from './VideoModal';

import { API } from '@/config';

// Category definitions with colors.
const CATEGORIES = {
  all: { en: 'All', ar: 'الكل', sv: 'Alla', color: 'bg-slate-500' },
  general: { en: 'General', ar: 'عام', sv: 'Allmänt', color: 'bg-slate-600' },
  traffic_signs: { en: 'Traffic Signs', ar: 'إشارات المرور', sv: 'Trafikskyltar', color: 'bg-purple-600' },
  traffic_rules: { en: 'Traffic Rules', ar: 'قواعد المرور', sv: 'Trafikregler', color: 'bg-blue-600' },
  road_rules: { en: 'Road Rules', ar: 'قواعد الطريق', sv: 'Vägregler', color: 'bg-indigo-600' },
  safety: { en: 'Safety', ar: 'السلامة', sv: 'Säkerhet', color: 'bg-green-600' },
  parking: { en: 'Parking', ar: 'الوقوف', sv: 'Parkering', color: 'bg-orange-600' },
  theory: { en: 'Theory', ar: 'نظري', sv: 'Teori', color: 'bg-violet-600' },
  warning: { en: 'Warning', ar: 'تحذيرية', sv: 'Varning', color: 'bg-yellow-600' },
  prohibition: { en: 'Prohibition', ar: 'منع', sv: 'Förbud', color: 'bg-red-600' },
  mandatory: { en: 'Mandatory', ar: 'إلزامية', sv: 'Obligatorisk', color: 'bg-blue-600' },
  informative: { en: 'Informative', ar: 'إرشادية', sv: 'Informativ', color: 'bg-cyan-600' }
};

// Helper: is a question/item classified as Taxi?
const isTaxiItem = (item) => (item?.licenseType || '').toLowerCase() === 'taxi';

const ContentManagement = ({ token, i18n: propI18n }) => {
  const { i18n: hookI18n, t } = useTranslation();
  const i18n = hookI18n || propI18n || { language: 'en' };
  const currentLang = i18n?.language || 'en';
  const [activeTab, setActiveTab] = useState('questions');
  const [langKey, setLangKey] = useState(Date.now()); // Force re-render on language change
  const [questions, setQuestions] = useState([]);
  const [lessons, setLessons] = useState([]);
  const [trafficSigns, setTrafficSigns] = useState([]);
  const [quickTips, setQuickTips] = useState([]);
  const [videos, setVideos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [imageFilter, setImageFilter] = useState('all'); // 'all', 'with_image', 'without_image'
  // Taxi tab sub-category filter: null = both, or 'sakerhet_och_beteende' | 'lagstiftning'
  const [taxiSubFilter, setTaxiSubFilter] = useState('all');
  
  // Modal states
  const [questionModalOpen, setQuestionModalOpen] = useState(false);
  const [lessonModalOpen, setLessonModalOpen] = useState(false);
  const [trafficSignModalOpen, setTrafficSignModalOpen] = useState(false);
  const [quickTipModalOpen, setQuickTipModalOpen] = useState(false);
  const [videoModalOpen, setVideoModalOpen] = useState(false);
  const [editingItem, setEditingItem] = useState(null);

  // Get category label
  const getCategoryLabel = (cat) => {
    const category = CATEGORIES[cat] || CATEGORIES.general;
    return category[i18n.language] || category.en;
  };

  const getCategoryColor = (cat) => {
    return CATEGORIES[cat]?.color || CATEGORIES.general.color;
  };

  // Helper function to get translated text
  const getTranslatedText = (textObj, fallback = 'N/A') => {
    if (!textObj) return fallback;
    if (typeof textObj === 'string') return textObj;
    
    // Get the actual current language
    const lang = currentLang || i18n?.language || 'sv';
    
    // Return the text for current language if exists
    if (textObj[lang]) {
      return textObj[lang];
    }
    
    // Fallback to Swedish then English
    return textObj.sv || textObj.en || fallback;
  };

  // Fetch content when component mounts or token changes
  useEffect(() => {
    if (token) {
      fetchAllContent();
    }
  }, [token]);

  // Re-render when language changes - NO data clearing to avoid flash
  useEffect(() => {
    const newKey = Date.now();
    setLangKey(newKey);
    console.log('Language changed to:', currentLang, 'key:', newKey, 'VERSION: 2026-03-02-SMOOTH');
    // Don't clear data - just update the key to trigger re-render with existing data
    // The translations are already in the question objects, so no refetch needed
  }, [currentLang]);

  // No need to refetch when tab changes - data is already loaded

  const fetchAllContent = async (retryCount = 0) => {
    if (!token) return;
    
    setLoading(true);
    
    // Hard timeout: 15 seconds max then show whatever we have
    const timeoutId = setTimeout(() => {
      setLoading(false);
      toast.warning(
        i18n.language === 'ar'
          ? 'تحميل البيانات يستغرق وقتاً طويلاً. جرب إعادة التحميل.'
          : 'Data loading is taking too long. Try refreshing.',
        { duration: 5000 }
      );
    }, 15000);
    
    try {
      const headers = { 
        Authorization: `Bearer ${token}`,
        'Cache-Control': 'no-cache',
        'Pragma': 'no-cache'
      };
      
      const timestamp = Date.now();
      const langParam = currentLang || i18n?.language || 'sv';
      
      const [questionsRes, lessonsRes, signsRes, tipsRes, videosRes] = await Promise.all([
        axios.get(`${API}/tests/questions?_t=${timestamp}&lang=${langParam}&licenseType=all`, { headers }).catch((err) => {
          console.log('Questions fetch error:', err?.response?.status, err?.message);
          return { data: [] };
        }),
        axios.get(`${API}/lessons?_t=${timestamp}`, { headers }).catch((err) => {
          console.log('Lessons fetch error:', err?.response?.status, err?.message);
          return { data: [] };
        }),
        axios.get(`${API}/admin/traffic-signs?_t=${timestamp}`, { headers }).catch((err) => {
          console.log('Traffic signs fetch error:', err?.response?.status, err?.message);
          return { data: [] };
        }),
        axios.get(`${API}/admin/quick-tips?_t=${timestamp}`, { headers }).catch((err) => {
          console.log('Quick tips fetch error:', err?.response?.status, err?.message);
          return { data: [] };
        }),
        axios.get(`${API}/videos?_t=${timestamp}`, { headers }).catch((err) => {
          console.log('Videos fetch error:', err?.response?.status, err?.message);
          return { data: [] };
        })
      ]);

      console.log('Fetched data:', {
        questions: questionsRes.data?.length,
        lessons: lessonsRes.data?.length,
        signs: signsRes.data?.length,
        tips: tipsRes.data?.length,
        videos: videosRes.data?.length
      });

      const q = Array.isArray(questionsRes.data) ? questionsRes.data : [];
      const l = Array.isArray(lessonsRes.data) ? lessonsRes.data : [];
      const s = Array.isArray(signsRes.data) ? signsRes.data : [];
      const t = Array.isArray(tipsRes.data) ? tipsRes.data : [];
      const v = Array.isArray(videosRes.data) ? videosRes.data : [];

      setQuestions(q);
      setLessons(l);
      setTrafficSigns(s);
      setQuickTips(t);
      setVideos(v);

      // Show warning if some data failed to load
      const failed = [];
      if (q.length === 0) failed.push(i18n.language === 'ar' ? 'الأسئلة' : 'Questions');
      if (l.length === 0) failed.push(i18n.language === 'ar' ? 'الدروس' : 'Lessons');
      if (s.length === 0) failed.push(i18n.language === 'ar' ? 'الإشارات' : 'Signs');
      if (t.length === 0) failed.push(i18n.language === 'ar' ? 'النصائح' : 'Tips');
      
      if (failed.length > 0 && failed.length < 4) {
        toast.warning(
          i18n.language === 'ar' 
            ? `تم تحميل بعض البيانات. فشل تحميل: ${failed.join('، ')} — جرب إعادة التحميل`
            : `Some data loaded. Failed: ${failed.join(', ')} — try refreshing`,
          { duration: 6000 }
        );
      }
      // Auto-retry once if all data failed
      if (failed.length === 4 && retryCount < 2) {
        console.log(`All data failed, auto-retrying (attempt ${retryCount + 1})...`);
        setTimeout(() => fetchAllContent(retryCount + 1), 2000);
        return;
      }
    } catch (error) {
      const status = error?.response?.status;
      const detail = error?.response?.data?.detail || error?.message || 'Unknown error';
      console.error('Error fetching content:', { status, detail, error });
      toast.error(
        i18n.language === 'ar' 
          ? `خطأ في جلب المحتوى: ${status ? `(${status}) ` : ''}${detail}` 
          : `Error fetching content: ${status ? `(${status}) ` : ''}${detail}`,
        { duration: 8000 }
      );
    } finally {
      clearTimeout(timeoutId);
      setLoading(false);
    }
  };

  // Lightweight refetch for single content type — no global loading spinner,
  // so the admin page doesn't feel like it "refreshes" after saving.
  const refetchQuestionsOnly = async () => {
    if (!token) return;
    try {
      const headers = { Authorization: `Bearer ${token}`, 'Cache-Control': 'no-cache' };
      const langParam = currentLang || i18n?.language || 'sv';
      const res = await axios.get(`${API}/tests/questions?_t=${Date.now()}&lang=${langParam}&licenseType=all`, { headers });
      if (Array.isArray(res.data)) setQuestions(res.data);
    } catch (e) {
      console.log('refetchQuestionsOnly error:', e?.message);
    }
  };

  const refetchLessonsOnly = async () => {
    if (!token) return;
    try {
      const headers = { Authorization: `Bearer ${token}`, 'Cache-Control': 'no-cache' };
      const res = await axios.get(`${API}/lessons?_t=${Date.now()}`, { headers });
      if (Array.isArray(res.data)) setLessons(res.data);
    } catch (e) { console.log('refetchLessonsOnly error:', e?.message); }
  };

  const refetchSignsOnly = async () => {
    if (!token) return;
    try {
      const headers = { Authorization: `Bearer ${token}`, 'Cache-Control': 'no-cache' };
      const res = await axios.get(`${API}/admin/traffic-signs?_t=${Date.now()}`, { headers });
      if (Array.isArray(res.data)) setTrafficSigns(res.data);
    } catch (e) { console.log('refetchSignsOnly error:', e?.message); }
  };

  const refetchTipsOnly = async () => {
    if (!token) return;
    try {
      const headers = { Authorization: `Bearer ${token}`, 'Cache-Control': 'no-cache' };
      const res = await axios.get(`${API}/admin/quick-tips?_t=${Date.now()}`, { headers });
      if (Array.isArray(res.data)) setQuickTips(res.data);
    } catch (e) { console.log('refetchTipsOnly error:', e?.message); }
  };

  const refetchVideosOnly = async () => {
    if (!token) return;
    try {
      const headers = { Authorization: `Bearer ${token}`, 'Cache-Control': 'no-cache' };
      const res = await axios.get(`${API}/videos?_t=${Date.now()}`, { headers });
      if (Array.isArray(res.data)) setVideos(res.data);
    } catch (e) { console.log('refetchVideosOnly error:', e?.message); }
  };

  const handleDelete = async (type, id) => {
    if (!window.confirm(i18n.language === 'ar' ? 'هل أنت متأكد من الحذف؟' : 'Are you sure you want to delete?')) {
      return;
    }

    try {
      const headers = { Authorization: `Bearer ${token}` };
      await axios.delete(`${API}/admin/${type}/${id}`, { headers });
      toast.success(i18n.language === 'ar' ? 'تم الحذف' : 'Deleted successfully');
      // Lightweight refetch for the specific type only (no full-page loader)
      if (type === 'questions') refetchQuestionsOnly();
      else if (type === 'lessons') refetchLessonsOnly();
      else if (type === 'traffic-signs') refetchSignsOnly();
      else if (type === 'quick-tips') refetchTipsOnly();
      else if (type === 'videos') refetchVideosOnly();
      else fetchAllContent();
    } catch (error) {
      console.error('Error deleting:', error);
      toast.error(i18n.language === 'ar' ? 'خطأ في الحذف' : 'Error deleting');
    }
  };

  const handleBulkDelete = async (ids) => {
    const count = ids.length;
    const msg = i18n.language === 'ar' 
      ? `هل أنت متأكد من حذف ${count} سؤال؟` 
      : `Are you sure you want to delete ${count} questions?`;
    if (!window.confirm(msg)) return;

    try {
      const headers = { Authorization: `Bearer ${token}` };
      const res = await axios.post(`${API}/admin/questions/bulk-delete`, { ids }, { headers });
      toast.success(
        i18n.language === 'ar' 
          ? `تم حذف ${res.data.deleted_count} سؤال بنجاح` 
          : `${res.data.deleted_count} questions deleted`
      );
      refetchQuestionsOnly();
    } catch (error) {
      console.error('Error bulk deleting:', error);
      toast.error(i18n.language === 'ar' ? 'خطأ في الحذف الجماعي' : 'Error bulk deleting');
    }
  };

  // Move a batch of questions to a different license category. Called from the
  // QuestionsTable bulk-action bar. Confirms first because it's a big change.
  const handleBulkSetLicense = async (ids, { licenseType, taxiCategory }) => {
    if (!ids || ids.length === 0) return;
    const catLabel = taxiCategory === 'sakerhet_och_beteende' ? 'Säkerhet och beteende'
      : taxiCategory === 'lagstiftning' ? 'Lagstiftning'
      : taxiCategory === 'kartlasning' ? 'Kartläsning'
      : '';
    const msg = i18n.language === 'ar'
      ? `نقل ${ids.length} سؤال إلى Taxi (${catLabel})؟`
      : `Move ${ids.length} question(s) to Taxi (${catLabel})?`;
    if (!window.confirm(msg)) return;
    try {
      const headers = { Authorization: `Bearer ${token}` };
      const res = await axios.post(
        `${API}/admin/questions/bulk-set-license`,
        { ids, licenseType, taxiCategory },
        { headers }
      );
      toast.success(
        i18n.language === 'ar'
          ? `تم نقل ${res.data.modified} سؤال إلى Taxi`
          : `${res.data.modified} question(s) moved to Taxi`
      );
      refetchQuestionsOnly();
    } catch (error) {
      console.error('bulk-set-license error:', error);
      toast.error(i18n.language === 'ar' ? 'خطأ في النقل' : 'Move failed');
    }
  };

  const handleAddQuestion = () => {
    setEditingItem(null);
    setAddLicenseDefault('b_korkort');
    setQuestionModalOpen(true);
  };

  // Track which license the "add question" modal should default to. Used by
  // the dedicated Taxifrågor tab so admins land on the right form.
  const [addLicenseDefault, setAddLicenseDefault] = useState('b_korkort');
  const handleAddTaxiQuestion = () => {
    setEditingItem(null);
    setAddLicenseDefault('taxi');
    setQuestionModalOpen(true);
  };

  const handleEditQuestion = (question) => {
    setEditingItem(question);
    setQuestionModalOpen(true);
  };

  const handleAddLesson = () => {
    setEditingItem(null);
    setLessonModalOpen(true);
  };

  const handleEditLesson = (lesson) => {
    setEditingItem(lesson);
    setLessonModalOpen(true);
  };

  const handleAddTrafficSign = () => {
    setEditingItem(null);
    setTrafficSignModalOpen(true);
  };

  const handleEditTrafficSign = (sign) => {
    setEditingItem(sign);
    setTrafficSignModalOpen(true);
  };

  const handleAddQuickTip = () => {
    setEditingItem(null);
    setQuickTipModalOpen(true);
  };

  const handleEditQuickTip = (tip) => {
    setEditingItem(tip);
    setQuickTipModalOpen(true);
  };

  const handleEditVideo = (video) => {
    setEditingItem(video);
    setVideoModalOpen(true);
  };

  // B-körkort questions list — used by the "Questions" tab. Taxi questions
  // are completely excluded here so admins never see them mixed.
  const filteredQuestions = questions.filter(q => {
    if (isTaxiItem(q)) return false;
    const matchesSearch = q.question?.[i18n.language]?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      q.question?.en?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesCategory = categoryFilter === 'all' || q.category === categoryFilter;
    const isValidImage = (url) => {
      return url && 
        url !== 'None' && 
        url !== '' && 
        url !== 'undefined' &&
        url !== 'null' &&
        !url.includes('undefined') &&
        (url.startsWith('http') || url.startsWith('/'));
    };
    
    const matchesImage = imageFilter === 'all' || 
      (imageFilter === 'with_image' && isValidImage(q.image_url)) ||
      (imageFilter === 'without_image' && !isValidImage(q.image_url));
    return matchesSearch && matchesCategory && matchesImage;
  });

  // Taxi-only questions list — used by the dedicated "Taxifrågor" tab.
  const taxiQuestions = questions.filter(isTaxiItem);
  const filteredTaxiQuestions = taxiQuestions.filter(q => {
    const matchesSearch = !searchTerm ||
      q.question?.[i18n.language]?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      q.question?.en?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesSub = taxiSubFilter === 'all' || (q.taxiCategory || '').toLowerCase() === taxiSubFilter;
    return matchesSearch && matchesSub;
  });

  // Count questions with images
  const isValidImageUrl = (url) => {
    return url && 
      url !== 'None' && 
      url !== '' && 
      url !== 'undefined' &&
      url !== 'null' &&
      !url.includes('undefined') &&
      (url.startsWith('http') || url.startsWith('/'));
  };
  // Image counters — only for the B-körkort "Questions" tab (Taxi excluded).
  const bQuestions = questions.filter(q => !isTaxiItem(q));
  const questionsWithImages = bQuestions.filter(q => isValidImageUrl(q.image_url)).length;
  const questionsWithoutImages = bQuestions.length - questionsWithImages;

  const filteredLessons = lessons.filter(l => {
    const matchesSearch = l.title?.[i18n.language]?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      l.title?.en?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesCategory = categoryFilter === 'all' || l.category === categoryFilter;
    return matchesSearch && matchesCategory;
  });

  const filteredTrafficSigns = trafficSigns.filter(s => {
    const matchesSearch = s.name?.[i18n.language]?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      s.name?.sv?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      s.name?.en?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesCategory = categoryFilter === 'all' || s.category === categoryFilter;
    return matchesSearch && matchesCategory;
  });

  const filteredQuickTips = quickTips.filter(t => {
    const matchesSearch = t.title?.[i18n.language]?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      t.title?.sv?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      t.title?.en?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesCategory = categoryFilter === 'all' || t.category === categoryFilter;
    return matchesSearch && matchesCategory;
  });

  const filteredVideos = videos.filter(v => {
    const search = searchTerm.toLowerCase();
    const matchesSearch = !search ||
      v.title?.[i18n.language]?.toLowerCase().includes(search) ||
      v.title?.sv?.toLowerCase().includes(search) ||
      v.title?.en?.toLowerCase().includes(search);
    const matchesCategory = categoryFilter === 'all' || v.category === categoryFilter;
    return matchesSearch && matchesCategory;
  });

  if (loading) {
    return (
      <div className="bg-white rounded-xl p-8 border border-slate-200">
        <div className="flex items-center justify-center">
          <div className="animate-spin w-8 h-8 border-4 border-emerald-500 border-t-transparent rounded-full"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-white rounded-xl p-6 border border-slate-200">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h3 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
              <FileText className="w-7 h-7 text-emerald-600" />
              {i18n.language === 'ar' ? 'إدارة المحتوى' : 'Content Management'}
            </h3>
            <p className="text-slate-500 mt-1">
              {i18n.language === 'ar' 
                ? `${questions.length} سؤال، ${lessons.length} درس، ${trafficSigns.length} إشارة`
                : `${questions.length} questions, ${lessons.length} lessons, ${trafficSigns.length} signs`
              }
            </p>
          </div>
          <Button 
            variant="outline" 
            onClick={() => fetchAllContent()}
            disabled={loading}
            className="flex items-center gap-2"
          >
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
            {i18n.language === 'ar' ? 'تحديث' : 'Refresh'}
          </Button>
        </div>

        {/* Search and Filter */}
        <div className="flex flex-col sm:flex-row gap-4">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
            <Input
              type="text"
              placeholder={i18n.language === 'ar' ? 'بحث في المحتوى...' : 'Search content...'}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10"
            />
          </div>
          <div className="flex items-center gap-2">
            <Filter className="w-5 h-5 text-slate-500" />
            <Select value={categoryFilter} onValueChange={setCategoryFilter}>
              <SelectTrigger className="w-[180px]">
                <SelectValue placeholder={i18n.language === 'ar' ? 'الفئة' : 'Category'} />
              </SelectTrigger>
              <SelectContent>
                {Object.entries(CATEGORIES).map(([key, value]) => (
                  <SelectItem key={key} value={key}>
                    <span className="flex items-center gap-2">
                      <span className={`w-2 h-2 rounded-full ${value.color}`}></span>
                      {value[i18n.language] || value.en}
                    </span>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>
        
        {/* Active Filter Badge */}
        {categoryFilter !== 'all' && (
          <div className="flex items-center gap-2 mt-4">
            <span className="text-sm text-slate-500">{i18n.language === 'ar' ? 'الفلتر النشط:' : 'Active filter:'}</span>
            <Badge className={`${getCategoryColor(categoryFilter)} text-white`}>
              {getCategoryLabel(categoryFilter)}
              <button 
                onClick={() => setCategoryFilter('all')} 
                className="ms-2 hover:text-slate-200"
              >
                ✕
              </button>
            </Badge>
          </div>
        )}
      </div>

      {/* Content Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="bg-white border border-slate-200 p-1 flex flex-wrap gap-1 h-auto">
          <TabsTrigger value="questions" className="flex items-center gap-2">
            <FileQuestion className="w-4 h-4" />
            {i18n.language === 'ar' ? 'أسئلة B-körkort' : 'B-körkort'} ({filteredQuestions.length}/{bQuestions.length})
          </TabsTrigger>
          <TabsTrigger value="taxi-questions" className="flex items-center gap-2 data-[state=active]:bg-amber-100 data-[state=active]:text-amber-900" data-testid="admin-tab-taxi">
            <span aria-hidden>🚕</span>
            {i18n.language === 'ar' ? 'أسئلة التكسي' : 'Taxifrågor'} ({filteredTaxiQuestions.length}/{taxiQuestions.length})
          </TabsTrigger>
          <TabsTrigger value="lessons" className="flex items-center gap-2">
            <BookOpen className="w-4 h-4" />
            {i18n.language === 'ar' ? 'الدروس' : 'Lessons'} ({filteredLessons.length}/{lessons.length})
          </TabsTrigger>
          <TabsTrigger value="quick-tips" className="flex items-center gap-2">
            <Lightbulb className="w-4 h-4" />
            {i18n.language === 'ar' ? 'نصائح سريعة' : 'Snabba Råd'} ({filteredQuickTips.length}/{quickTips.length})
          </TabsTrigger>
          <TabsTrigger value="traffic-signs" className="flex items-center gap-2">
            <AlertTriangle className="w-4 h-4" />
            {i18n.language === 'ar' ? 'الإشارات' : 'Signs'} ({filteredTrafficSigns.length}/{trafficSigns.length})
          </TabsTrigger>
          <TabsTrigger value="videos" className="flex items-center gap-2">
            <VideoIcon className="w-4 h-4" />
            {i18n.language === 'ar' ? 'الفيديوهات' : 'Videor'} ({filteredVideos.length}/{videos.length})
          </TabsTrigger>
          <TabsTrigger value="staged" className="flex items-center gap-2" data-testid="admin-tab-staged">
            <span aria-hidden>🔒</span>
            {i18n.language === 'ar' ? 'قسم خاص (مخفي)' : 'Dolda frågor'}
          </TabsTrigger>
        </TabsList>

        <TabsContent value="questions">
          {/* Alert for questions needing images */}
          {questionsWithoutImages > 0 && (
            <div className="mb-4 p-4 bg-amber-50 border border-amber-200 rounded-lg flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-amber-100 rounded-full flex items-center justify-center">
                  <ImageIcon className="w-5 h-5 text-amber-600" />
                </div>
                <div>
                  <p className="font-semibold text-amber-800">
                    {i18n.language === 'ar' ? 'أسئلة تحتاج صور' : 'Frågor behöver bilder'}
                  </p>
                  <p className="text-sm text-amber-600">
                    {i18n.language === 'ar' 
                      ? `${questionsWithoutImages} سؤال بدون صورة من أصل ${questions.length}` 
                      : `${questionsWithoutImages} frågor utan bild av ${questions.length}`}
                  </p>
                </div>
              </div>
              <Button 
                variant="outline" 
                size="sm"
                className="border-amber-300 text-amber-700 hover:bg-amber-100"
                onClick={() => setImageFilter('without_image')}
              >
                {i18n.language === 'ar' ? 'عرض الأسئلة' : 'Visa frågor'}
              </Button>
            </div>
          )}
          
          <PurgeImageQuestionsBar token={token} isAr={i18n.language === 'ar'} onDone={() => refetchQuestionsOnly()} />
          <div className="mb-4 flex flex-wrap justify-between items-center gap-4">
            {/* Image Filter */}
            <div className="flex items-center gap-2">
              <ImageIcon className="w-4 h-4 text-slate-500" />
              <Select value={imageFilter} onValueChange={setImageFilter}>
                <SelectTrigger className="w-[200px]">
                  <SelectValue placeholder="Filter by image" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">
                    {i18n.language === 'ar' ? 'الكل' : 'Alla'} ({questions.length})
                  </SelectItem>
                  <SelectItem value="with_image">
                    <span className="flex items-center gap-2">
                      <span className="w-2 h-2 bg-green-500 rounded-full"></span>
                      {i18n.language === 'ar' ? 'مع صورة' : 'Med bild'} ({questionsWithImages})
                    </span>
                  </SelectItem>
                  <SelectItem value="without_image">
                    <span className="flex items-center gap-2">
                      <span className="w-2 h-2 bg-red-500 rounded-full animate-pulse"></span>
                      {i18n.language === 'ar' ? 'بدون صورة' : 'Utan bild'} ({questionsWithoutImages})
                    </span>
                  </SelectItem>
                </SelectContent>
              </Select>
            </div>
            <Button onClick={handleAddQuestion} className="bg-gradient-to-r from-emerald-500 to-teal-500 hover:from-emerald-600 hover:to-teal-600">
              <Plus className="w-4 h-4 me-2" />
              {i18n.language === 'ar' ? 'إضافة سؤال' : 'Lägg till fråga'}
            </Button>
          </div>
          <QuestionsTable 
            key={`questions-${currentLang}-${langKey}`}
            questions={filteredQuestions} 
            onDelete={(id) => handleDelete('questions', id)}
            onBulkDelete={handleBulkDelete}
            onBulkSetLicense={handleBulkSetLicense}
            allowMoveToTaxi={true}
            onEdit={handleEditQuestion}
            i18n={i18n}
            currentLang={currentLang}
            getCategoryLabel={getCategoryLabel}
            getCategoryColor={getCategoryColor}
          />
        </TabsContent>

        <TabsContent value="taxi-questions" data-testid="admin-taxi-panel">
          <div className="mb-4 flex flex-wrap justify-between items-center gap-4">
            <div className="flex items-center gap-2">
              <span className="text-2xl" aria-hidden>🚕</span>
              <Select value={taxiSubFilter} onValueChange={setTaxiSubFilter}>
                <SelectTrigger className="w-[260px]" data-testid="admin-taxi-sub-filter">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">
                    {i18n.language === 'ar' ? 'كل أقسام التكسي' : 'Alla Taxi-avdelningar'} ({taxiQuestions.length})
                  </SelectItem>
                  <SelectItem value="sakerhet_och_beteende">
                    <span className="flex items-center gap-2">
                      <span className="w-2 h-2 rounded-full bg-rose-500"></span>
                      Säkerhet och beteende ({taxiQuestions.filter(q => q.taxiCategory === 'sakerhet_och_beteende').length})
                    </span>
                  </SelectItem>
                  <SelectItem value="lagstiftning">
                    <span className="flex items-center gap-2">
                      <span className="w-2 h-2 rounded-full bg-indigo-500"></span>
                      Lagstiftning ({taxiQuestions.filter(q => q.taxiCategory === 'lagstiftning').length})
                    </span>
                  </SelectItem>
                </SelectContent>
              </Select>
            </div>
            <Button
              onClick={handleAddTaxiQuestion}
              data-testid="admin-add-taxi-question-btn"
              className="bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600 text-white"
            >
              <Plus className="w-4 h-4 me-2" />
              {i18n.language === 'ar' ? 'إضافة سؤال تكسي' : 'Lägg till Taxifråga'}
            </Button>
          </div>
          {taxiQuestions.length === 0 ? (
            <div className="text-center py-16 bg-amber-50 border-2 border-dashed border-amber-200 rounded-2xl">
              <div className="text-5xl mb-3" aria-hidden>🚕</div>
              <p className="text-amber-800 font-semibold">
                {i18n.language === 'ar' ? 'لا توجد أسئلة تكسي بعد' : 'Inga Taxifrågor än'}
              </p>
              <p className="text-amber-600 text-sm mt-1">
                {i18n.language === 'ar'
                  ? 'اضغط "إضافة سؤال تكسي" للبدء'
                  : 'Klicka på "Lägg till Taxifråga" för att börja'}
              </p>
            </div>
          ) : (
            <QuestionsTable
              key={`taxi-questions-${currentLang}-${langKey}`}
              questions={filteredTaxiQuestions}
              onDelete={(id) => handleDelete('questions', id)}
              onBulkDelete={handleBulkDelete}
              onEdit={handleEditQuestion}
              i18n={i18n}
              currentLang={currentLang}
              getCategoryLabel={getCategoryLabel}
              getCategoryColor={getCategoryColor}
            />
          )}
        </TabsContent>

        <TabsContent value="staged">
          <StagedQuestionsPanel token={token} isAr={i18n.language === 'ar'} />
        </TabsContent>

        <TabsContent value="lessons">
          <div className="mb-4 flex justify-end">
            <Button onClick={handleAddLesson} className="bg-gradient-to-r from-emerald-500 to-teal-500 hover:from-emerald-600 hover:to-teal-600">
              <Plus className="w-4 h-4 me-2" />
              {i18n.language === 'ar' ? 'إضافة درس' : 'Add Lesson'}
            </Button>
          </div>
          <LessonsTable 
            lessons={filteredLessons} 
            onDelete={(id) => handleDelete('lessons', id)}
            onEdit={handleEditLesson}
            i18n={i18n}
            getCategoryLabel={getCategoryLabel}
            getCategoryColor={getCategoryColor}
          />
        </TabsContent>

        <TabsContent value="quick-tips">
          <div className="mb-4 flex justify-end gap-2">
            <Button
              onClick={() => window.open('/quick-tips', '_blank')}
              variant="outline"
              className="border-amber-300 text-amber-600 hover:bg-amber-50"
            >
              <ExternalLink className="w-4 h-4 me-2" />
              {i18n.language === 'ar' ? 'عرض الصفحة' : 'View Page'}
            </Button>
            <Button onClick={handleAddQuickTip} className="bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600">
              <Plus className="w-4 h-4 me-2" />
              {i18n.language === 'ar' ? 'إضافة نصيحة' : 'Add Tip'}
            </Button>
          </div>
          <QuickTipsTable 
            tips={filteredQuickTips} 
            onDelete={(id) => handleDelete('quick-tips', id)}
            onEdit={handleEditQuickTip}
            i18n={i18n}
            getCategoryLabel={getCategoryLabel}
            getCategoryColor={getCategoryColor}
          />
        </TabsContent>

        <TabsContent value="traffic-signs">
          <div className="mb-4 flex justify-end">
            <Button onClick={handleAddTrafficSign} className="bg-gradient-to-r from-emerald-500 to-teal-500 hover:from-emerald-600 hover:to-teal-600">
              <Plus className="w-4 h-4 me-2" />
              {i18n.language === 'ar' ? 'إضافة إشارة' : 'Add Sign'}
            </Button>
          </div>
          <TrafficSignsTable 
            signs={filteredTrafficSigns} 
            onDelete={(id) => handleDelete('traffic-signs', id)}
            onEdit={handleEditTrafficSign}
            i18n={i18n}
            getCategoryLabel={getCategoryLabel}
            getCategoryColor={getCategoryColor}
          />
        </TabsContent>

        <TabsContent value="videos">
          <div className="mb-4 flex justify-end gap-2">
            <Button
              onClick={() => window.open('/teorivideor', '_blank')}
              variant="outline"
              className="border-purple-300 text-purple-600 hover:bg-purple-50"
            >
              <ExternalLink className="w-4 h-4 me-2" />
              {i18n.language === 'ar' ? 'عرض الصفحة' : 'View Page'}
            </Button>
            <Button
              onClick={() => { setEditingItem(null); setVideoModalOpen(true); }}
              className="bg-gradient-to-r from-purple-500 to-pink-500 hover:from-purple-600 hover:to-pink-600"
              data-testid="add-video-btn"
            >
              <Plus className="w-4 h-4 me-2" />
              {i18n.language === 'ar' ? 'إضافة فيديو' : 'Lägg till video'}
            </Button>
          </div>
          <VideosTable
            videos={filteredVideos}
            onDelete={(id) => handleDelete('videos', id)}
            onEdit={handleEditVideo}
            i18n={i18n}
          />
        </TabsContent>
      </Tabs>

      {/* Modals */}
      {/* Question Modal - Import from existing component */}
      {questionModalOpen && (
        <QuestionModal
          open={questionModalOpen}
          onClose={() => {
            setQuestionModalOpen(false);
            setEditingItem(null);
          }}
          question={editingItem}
          defaultLicenseType={addLicenseDefault}
          lockLicenseType={true}
          token={token}
          onSuccess={() => {
            refetchQuestionsOnly();
            setQuestionModalOpen(false);
            setEditingItem(null);
          }}
          i18n={i18n}
          t={() => {}}
        />
      )}

      {/* Lesson Modal */}
      {lessonModalOpen && (
        <LessonModal
          open={lessonModalOpen}
          onClose={() => {
            setLessonModalOpen(false);
            setEditingItem(null);
          }}
          lesson={editingItem}
          token={token}
          onSuccess={() => {
            refetchLessonsOnly();
            setLessonModalOpen(false);
            setEditingItem(null);
          }}
          i18n={i18n}
          t={() => {}}
        />
      )}

      {/* Traffic Sign Modal */}
      {trafficSignModalOpen && (
        <TrafficSignModal
          open={trafficSignModalOpen}
          onClose={() => {
            setTrafficSignModalOpen(false);
            setEditingItem(null);
          }}
          sign={editingItem}
          token={token}
          onSuccess={() => {
            refetchSignsOnly();
            setTrafficSignModalOpen(false);
            setEditingItem(null);
          }}
          i18n={i18n}
        />
      )}
      {quickTipModalOpen && (
        <QuickTipModal
          open={quickTipModalOpen}
          onClose={() => {
            setQuickTipModalOpen(false);
            setEditingItem(null);
          }}
          tip={editingItem}
          token={token}
          onSuccess={() => {
            refetchTipsOnly();
            setQuickTipModalOpen(false);
            setEditingItem(null);
          }}
          i18n={i18n}
        />
      )}
      {videoModalOpen && (
        <VideoModal
          open={videoModalOpen}
          onClose={() => {
            setVideoModalOpen(false);
            setEditingItem(null);
          }}
          video={editingItem}
          token={token}
          onSuccess={() => {
            refetchVideosOnly();
            setVideoModalOpen(false);
            setEditingItem(null);
          }}
          i18n={i18n}
          t={() => {}}
        />
      )}
    </div>
  );
};

// Traffic Sign Modal Component with Auto-Translation
const TrafficSignModal = ({ open, onClose, sign, token, onSuccess, i18n }) => {
  const [inputLanguage, setInputLanguage] = useState('sv');
  const [name, setName] = useState(sign?.name?.sv || sign?.name?.en || sign?.name?.ar || '');
  const [description, setDescription] = useState(sign?.description?.sv || sign?.description?.en || sign?.description?.ar || '');
  const [category, setCategory] = useState(sign?.category || 'warning');
  const [imageUrl, setImageUrl] = useState(sign?.image_url || '');
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);
  const fileInputRef = useRef(null);

  const languages = [
    { code: 'sv', name: 'Svenska', flag: '🇸🇪' },
    { code: 'en', name: 'English', flag: '🇬🇧' },
    { code: 'ar', name: 'العربية', flag: '🇸🇦' },
    { code: 'tr', name: 'Türkçe', flag: '🇹🇷' },
    { code: 'fa', name: 'فارسی', flag: '🇮🇷' },
    { code: 'so', name: 'Soomaali', flag: '🇸🇴' },
    { code: 'pl', name: 'Polski', flag: '🇵🇱' },
    { code: 'es', name: 'Español', flag: '🇪🇸' },
    { code: 'de', name: 'Deutsch', flag: '🇩🇪' }
  ];

  const categories = [
    { value: 'warning', label: { en: 'Warning', ar: 'تحذيرية', sv: 'Varning' } },
    { value: 'prohibition', label: { en: 'Prohibition', ar: 'منع', sv: 'Förbud' } },
    { value: 'mandatory', label: { en: 'Mandatory', ar: 'إلزامية', sv: 'Obligatorisk' } },
    { value: 'informative', label: { en: 'Informative', ar: 'إرشادية', sv: 'Informativ' } }
  ];

  const handleImageUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    setUploading(true);
    const uploadFormData = new FormData();
    uploadFormData.append('file', file);

    try {
      const response = await axios.post(`${API}/upload/image`, uploadFormData, {
        headers: { 
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        }
      });
      setImageUrl(response.data.url);
      toast.success(i18n.language === 'ar' ? 'تم رفع الصورة' : 'Image uploaded');
    } catch (error) {
      console.error('Upload error:', error);
      toast.error(i18n.language === 'ar' ? 'خطأ في رفع الصورة' : 'Error uploading image');
    } finally {
      setUploading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!name.trim()) {
      toast.error(i18n.language === 'ar' ? 'يرجى إدخال اسم الإشارة' : 'Please enter sign name');
      return;
    }

    setSaving(true);

    try {
      const payload = {
        input_language: inputLanguage,
        name: name.trim(),
        description: description.trim(),
        category: category,
        image_url: imageUrl
      };

      const headers = { Authorization: `Bearer ${token}` };

      if (sign?.id) {
        await axios.put(`${API}/admin/traffic-signs/${sign.id}`, payload, { headers });
        toast.success(i18n.language === 'ar' ? 'تم تحديث الإشارة' : 'Sign updated');
      } else {
        await axios.post(`${API}/admin/traffic-signs/auto-translate`, payload, { headers });
        toast.success(i18n.language === 'ar' ? 'تم إضافة الإشارة مع الترجمة التلقائية' : 'Sign added with auto-translation');
      }
      onSuccess();
    } catch (error) {
      console.error('Save error:', error);
      toast.error(i18n.language === 'ar' ? 'خطأ في حفظ الإشارة' : 'Error saving sign');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>
            {sign ? (i18n.language === 'ar' ? 'تعديل الإشارة' : 'Edit Sign') : (i18n.language === 'ar' ? 'إضافة إشارة جديدة' : 'Add New Sign')}
          </DialogTitle>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Image Upload */}
          <div className="space-y-2">
            <Label>{i18n.language === 'ar' ? 'صورة الإشارة' : 'Sign Image'}</Label>
            <div className="flex items-center gap-4">
              {imageUrl ? (
                <div className="relative">
                  <img src={imageUrl} alt="Sign" className="w-24 h-24 object-contain border rounded" />
                  <button
                    type="button"
                    onClick={() => setImageUrl('')}
                    className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-1"
                  >
                    <X className="w-3 h-3" />
                  </button>
                </div>
              ) : (
                <div 
                  onClick={() => fileInputRef.current?.click()}
                  className="w-24 h-24 border-2 border-dashed border-slate-300 rounded flex items-center justify-center cursor-pointer hover:border-emerald-500 transition-colors"
                >
                  {uploading ? (
                    <Loader2 className="w-6 h-6 text-slate-400 animate-spin" />
                  ) : (
                    <Upload className="w-6 h-6 text-slate-400" />
                  )}
                </div>
              )}
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                onChange={handleImageUpload}
                className="hidden"
              />
              <Button type="button" variant="outline" onClick={() => fileInputRef.current?.click()} disabled={uploading}>
                {uploading ? (i18n.language === 'ar' ? 'جاري الرفع...' : 'Uploading...') : (i18n.language === 'ar' ? 'اختر صورة' : 'Choose Image')}
              </Button>
            </div>
          </div>

          {/* Input Language Selection */}
          <div className="space-y-2">
            <Label>{i18n.language === 'ar' ? 'لغة الإدخال' : 'Input Language'}</Label>
            <Select value={inputLanguage} onValueChange={setInputLanguage}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {languages.map(lang => (
                  <SelectItem key={lang.code} value={lang.code}>
                    <span className="flex items-center gap-2">
                      <span>{lang.flag}</span>
                      <span>{lang.name}</span>
                    </span>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <p className="text-xs text-slate-500">
              {i18n.language === 'ar' ? '✨ سيتم ترجمة النص تلقائياً للغات الأخرى' : '✨ Text will be auto-translated to other languages'}
            </p>
          </div>

          {/* Category */}
          <div className="space-y-2">
            <Label>{i18n.language === 'ar' ? 'الفئة' : 'Category'}</Label>
            <Select value={category} onValueChange={setCategory}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {categories.map(cat => (
                  <SelectItem key={cat.value} value={cat.value}>
                    {cat.label[i18n.language] || cat.label.en}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Name */}
          <div className="space-y-2">
            <Label>{i18n.language === 'ar' ? 'اسم الإشارة' : 'Sign Name'} *</Label>
            <Input
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder={inputLanguage === 'ar' ? 'مثال: إشارة تحذيرية' : inputLanguage === 'sv' ? 'Exempel: Varningsskylt' : 'Example: Warning Sign'}
              dir={inputLanguage === 'ar' ? 'rtl' : 'ltr'}
              required
            />
          </div>

          {/* Description */}
          <div className="space-y-2">
            <Label>{i18n.language === 'ar' ? 'الوصف' : 'Description'}</Label>
            <Textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder={inputLanguage === 'ar' ? 'وصف الإشارة...' : inputLanguage === 'sv' ? 'Beskrivning av skylten...' : 'Description of the sign...'}
              rows={3}
              dir={inputLanguage === 'ar' ? 'rtl' : 'ltr'}
            />
          </div>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose}>
              {i18n.language === 'ar' ? 'إلغاء' : 'Cancel'}
            </Button>
            <Button type="submit" disabled={saving || !name.trim()}>
              {saving ? (
                <>
                  <Loader2 className="w-4 h-4 me-2 animate-spin" />
                  {i18n.language === 'ar' ? 'جاري الحفظ...' : 'Saving...'}
                </>
              ) : (
                sign ? (i18n.language === 'ar' ? 'تحديث' : 'Update') : (i18n.language === 'ar' ? 'إضافة' : 'Add')
              )}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

// Quick Tip Modal Component with Auto-Translation
const QuickTipModal = ({ open, onClose, tip, token, onSuccess, i18n }) => {
  const [inputLanguage, setInputLanguage] = useState('sv');
  const [title, setTitle] = useState(tip?.title?.sv || tip?.title?.en || tip?.title?.ar || '');
  const [content, setContent] = useState(tip?.content?.sv || tip?.content?.en || tip?.content?.ar || '');
  const [category, setCategory] = useState(tip?.category || 'general');
  const [icon, setIcon] = useState(tip?.icon || 'Lightbulb');
  const [color, setColor] = useState(tip?.color || 'bg-blue-500');
  const [saving, setSaving] = useState(false);

  const languages = [
    { code: 'sv', name: 'Svenska', flag: '🇸🇪' },
    { code: 'en', name: 'English', flag: '🇬🇧' },
    { code: 'ar', name: 'العربية', flag: '🇸🇦' },
    { code: 'tr', name: 'Türkçe', flag: '🇹🇷' },
    { code: 'fa', name: 'فارسی', flag: '🇮🇷' },
    { code: 'so', name: 'Soomaali', flag: '🇸🇴' },
    { code: 'pl', name: 'Polski', flag: '🇵🇱' },
    { code: 'es', name: 'Español', flag: '🇪🇸' },
    { code: 'de', name: 'Deutsch', flag: '🇩🇪' },
  ];

  const categories = [
    { value: 'speed', label: { ar: 'السرعة', sv: 'Hastighet', en: 'Speed' } },
    { value: 'safety', label: { ar: 'السلامة', sv: 'Säkerhet', en: 'Safety', tr: 'Güvenlik', fa: 'ایمنی', so: 'Badbaado', pl: 'Bezpieczeństwo', es: 'Seguridad', de: 'Sicherheit' } },
    { value: 'rules', label: { ar: 'القواعد', sv: 'Regler', en: 'Rules', tr: 'Kurallar', fa: 'قوانین', so: 'Xeerarka', pl: 'Przepisy', es: 'Reglas', de: 'Regeln' } },
    { value: 'signs', label: { ar: 'الإشارات', sv: 'Skyltar', en: 'Signs', tr: 'İşaretler', fa: 'علائم', so: 'Calaamadaha', pl: 'Znaki', es: 'Señales', de: 'Schilder' } },
    { value: 'weather', label: { ar: 'الطقس', sv: 'Väder', en: 'Weather', tr: 'Hava', fa: 'آب و هوا', so: 'Cimilada', pl: 'Pogoda', es: 'Clima', de: 'Wetter' } },
    { value: 'parking', label: { ar: 'الوقوف', sv: 'Parkering', en: 'Parking', tr: 'Park', fa: 'پارکینگ', so: 'Baarkinka', pl: 'Parkowanie', es: 'Aparcamiento', de: 'Parken' } },
    { value: 'exam', label: { ar: 'الاختبار', sv: 'Prov', en: 'Exam', tr: 'Sınav', fa: 'آزمون', so: 'Imtixaanka', pl: 'Egzamin', es: 'Examen', de: 'Prüfung' } },
    { value: 'vehicle', label: { ar: 'السيارة', sv: 'Fordon', en: 'Vehicle', tr: 'Araç', fa: 'خودرو', so: 'Gaariga', pl: 'Pojazd', es: 'Vehículo', de: 'Fahrzeug' } },
    { value: 'eco', label: { ar: 'بيئي', sv: 'Miljö', en: 'Eco', tr: 'Çevre', fa: 'زیست محیطی', so: 'Deegaanka', pl: 'Ekologia', es: 'Ecología', de: 'Umwelt' } },
    { value: 'general', label: { ar: 'عام', sv: 'Allmänt', en: 'General', tr: 'Genel', fa: 'عمومی', so: 'Guud', pl: 'Ogólne', es: 'General', de: 'Allgemein' } },
  ];

  const colors = [
    { value: 'bg-blue-500', label: 'Blue' },
    { value: 'bg-green-500', label: 'Green' },
    { value: 'bg-red-500', label: 'Red' },
    { value: 'bg-yellow-500', label: 'Yellow' },
    { value: 'bg-purple-500', label: 'Purple' },
    { value: 'bg-orange-500', label: 'Orange' },
    { value: 'bg-pink-500', label: 'Pink' },
    { value: 'bg-cyan-500', label: 'Cyan' },
    { value: 'bg-amber-500', label: 'Amber' },
    { value: 'bg-emerald-500', label: 'Emerald' },
  ];

  useEffect(() => {
    if (tip) {
      setTitle(tip.title?.[inputLanguage] || tip.title?.sv || tip.title?.en || '');
      setContent(tip.content?.[inputLanguage] || tip.content?.sv || tip.content?.en || '');
      setCategory(tip.category || 'general');
      setIcon(tip.icon || 'Lightbulb');
      setColor(tip.color || 'bg-blue-500');
    } else {
      setTitle('');
      setContent('');
      setCategory('general');
      setIcon('Lightbulb');
      setColor('bg-blue-500');
    }
  }, [tip, inputLanguage]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);

    try {
      const headers = { Authorization: `Bearer ${token}` };
      
      if (tip) {
        // Update existing tip
        await axios.put(`${API}/admin/quick-tips/${tip.id}`, {
          title: { ...tip.title, [inputLanguage]: title },
          content: { ...tip.content, [inputLanguage]: content },
          category,
          icon,
          color,
          input_language: inputLanguage
        }, { headers });
        toast.success(i18n.language === 'ar' ? 'تم تحديث النصيحة' : 'Tip updated');
      } else {
        // Create new tip with auto-translation
        await axios.post(`${API}/admin/quick-tips/auto-translate`, {
          title,
          content,
          category,
          icon,
          color,
          input_language: inputLanguage
        }, { headers });
        toast.success(i18n.language === 'ar' ? 'تم إضافة النصيحة' : 'Tip added');
      }
      
      onSuccess();
    } catch (error) {
      console.error('Error saving tip:', error);
      toast.error(i18n.language === 'ar' ? 'خطأ في الحفظ' : 'Error saving');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>
            {tip 
              ? (i18n.language === 'ar' ? 'تعديل النصيحة' : 'Edit Tip')
              : (i18n.language === 'ar' ? 'إضافة نصيحة جديدة' : 'Add New Tip')
            }
          </DialogTitle>
        </DialogHeader>
        
        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Input Language */}
          <div className="space-y-2">
            <Label>{i18n.language === 'ar' ? 'لغة الإدخال' : 'Input Language'}</Label>
            <Select value={inputLanguage} onValueChange={setInputLanguage}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {languages.map((lang) => (
                  <SelectItem key={lang.code} value={lang.code}>
                    {lang.flag} {lang.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <p className="text-xs text-slate-500">
              {i18n.language === 'ar' ? '✨ سيتم ترجمة النص تلقائياً لـ 9 لغات' : '✨ Text will be auto-translated to 9 languages'}
            </p>
          </div>

          {/* Category */}
          <div className="space-y-2">
            <Label>{i18n.language === 'ar' ? 'الفئة' : 'Category'}</Label>
            <Select value={category} onValueChange={setCategory}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {categories.map(cat => (
                  <SelectItem key={cat.value} value={cat.value}>
                    {cat.label[i18n.language] || cat.label.en}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Color */}
          <div className="space-y-2">
            <Label>{i18n.language === 'ar' ? 'اللون' : 'Color'}</Label>
            <Select value={color} onValueChange={setColor}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {colors.map(c => (
                  <SelectItem key={c.value} value={c.value}>
                    <div className="flex items-center gap-2">
                      <div className={`w-4 h-4 rounded ${c.value}`}></div>
                      {c.label}
                    </div>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Title */}
          <div className="space-y-2">
            <Label>{i18n.language === 'ar' ? 'العنوان' : 'Title'} *</Label>
            <Input
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder={inputLanguage === 'ar' ? 'عنوان النصيحة...' : inputLanguage === 'sv' ? 'Tips titel...' : 'Tip title...'}
              dir={inputLanguage === 'ar' ? 'rtl' : 'ltr'}
              required
            />
          </div>

          {/* Content */}
          <div className="space-y-2">
            <Label>{i18n.language === 'ar' ? 'المحتوى' : 'Content'} *</Label>
            <Textarea
              value={content}
              onChange={(e) => setContent(e.target.value)}
              placeholder={inputLanguage === 'ar' ? 'محتوى النصيحة...' : inputLanguage === 'sv' ? 'Tips innehåll...' : 'Tip content...'}
              rows={6}
              dir={inputLanguage === 'ar' ? 'rtl' : 'ltr'}
              required
            />
          </div>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose}>
              {i18n.language === 'ar' ? 'إلغاء' : 'Cancel'}
            </Button>
            <Button type="submit" disabled={saving || !title.trim() || !content.trim()}>
              {saving ? (
                <>
                  <Loader2 className="w-4 h-4 me-2 animate-spin" />
                  {i18n.language === 'ar' ? 'جاري الحفظ...' : 'Saving...'}
                </>
              ) : (
                tip ? (i18n.language === 'ar' ? 'تحديث' : 'Update') : (i18n.language === 'ar' ? 'إضافة' : 'Add')
              )}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

// Questions Table Component
const QuestionsTable = ({ questions, onDelete, onBulkDelete, onEdit, i18n, currentLang, getCategoryLabel, getCategoryColor, onBulkSetLicense, allowMoveToTaxi = true }) => {
  // Force re-render when language changes by using the lang in render
  const activeLang = currentLang || i18n?.language || 'sv';
  const [selectedIds, setSelectedIds] = React.useState([]);
  
  const allSelected = questions.length > 0 && selectedIds.length === questions.length;
  const someSelected = selectedIds.length > 0;

  const toggleSelect = (id) => {
    setSelectedIds(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
  };

  const toggleAll = () => {
    if (allSelected) {
      setSelectedIds([]);
    } else {
      setSelectedIds(questions.map(q => q.id));
    }
  };

  const handleBulkDeleteClick = () => {
    if (onBulkDelete && selectedIds.length > 0) {
      onBulkDelete(selectedIds);
      setSelectedIds([]);
    }
  };

  // Bulk reassign selected questions to a Taxi sub-category. Used when an
  // admin realises they saved Taxi content under B-körkort by mistake.
  const [movePickerOpen, setMovePickerOpen] = React.useState(false);
  const handleMoveToTaxi = async (taxiCategory) => {
    if (!onBulkSetLicense || selectedIds.length === 0) return;
    await onBulkSetLicense(selectedIds, { licenseType: 'taxi', taxiCategory });
    setSelectedIds([]);
    setMovePickerOpen(false);
  };
  
  // Helper function to get translated text
  const getQuestionText = (textObj) => {
    if (!textObj) return 'N/A';
    if (typeof textObj === 'string') return textObj;
    // Use activeLang from component scope
    return textObj[activeLang] || textObj['sv'] || textObj['en'] || 'N/A';
  };

  return (
  <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
    {/* Bulk Actions Bar - Sticky */}
    {someSelected && (
      <div className="flex items-center gap-3 px-4 py-3 bg-red-50 border-b border-red-200 sticky top-0 z-10" data-testid="bulk-actions-bar">
        <span className="text-sm font-medium text-red-700">
          {i18n.language === 'ar' ? `تم تحديد ${selectedIds.length} سؤال` : `${selectedIds.length} selected`}
        </span>
        <Button
          size="sm"
          variant="destructive"
          onClick={handleBulkDeleteClick}
          className="bg-red-600 hover:bg-red-700"
          data-testid="bulk-delete-btn"
        >
          <Trash2 className="w-4 h-4 me-1" />
          {i18n.language === 'ar' ? 'حذف المحدد' : 'Delete Selected'}
        </Button>
        {allowMoveToTaxi && onBulkSetLicense && (
          <div className="relative">
            <Button
              size="sm"
              onClick={() => setMovePickerOpen((v) => !v)}
              className="bg-amber-500 hover:bg-amber-600 text-white"
              data-testid="bulk-move-to-taxi-btn"
            >
              🚕 {i18n.language === 'ar' ? 'نقل إلى Taxi' : 'Flytta till Taxi'} ▾
            </Button>
            {movePickerOpen && (
              <div className="absolute mt-2 z-30 bg-white border border-amber-200 rounded-lg shadow-xl p-2 min-w-[240px]" data-testid="taxi-move-picker">
                <div className="text-xs text-slate-500 px-2 py-1">
                  {i18n.language === 'ar' ? 'اختر القسم الفرعي:' : 'Välj område:'}
                </div>
                {[
                  { key: 'sakerhet_och_beteende', emoji: '🛡️', sv: 'Säkerhet och beteende', ar: 'الأمان والسلوك' },
                  { key: 'lagstiftning', emoji: '⚖️', sv: 'Lagstiftning', ar: 'التشريعات' },
                ].map((c) => (
                  <button
                    key={c.key}
                    type="button"
                    onClick={() => handleMoveToTaxi(c.key)}
                    data-testid={`taxi-move-target-${c.key}`}
                    className="w-full text-left rtl:text-right px-3 py-2 rounded-md hover:bg-amber-50 flex items-center gap-2"
                  >
                    <span className="text-lg">{c.emoji}</span>
                    <span className="text-sm font-semibold text-slate-800">
                      {i18n.language === 'ar' ? c.ar : c.sv}
                    </span>
                  </button>
                ))}
              </div>
            )}
          </div>
        )}
        <Button
          size="sm"
          variant="ghost"
          onClick={() => setSelectedIds([])}
          className="text-slate-600"
        >
          {i18n.language === 'ar' ? 'إلغاء التحديد' : 'Deselect'}
        </Button>
      </div>
    )}
    <div className="overflow-x-auto">
      <table className="w-full">
        <thead className="bg-slate-50 border-b border-slate-200">
          <tr>
            <th className="px-3 py-4 w-10">
              <input
                type="checkbox"
                checked={allSelected}
                onChange={toggleAll}
                className="w-4 h-4 rounded border-slate-300 text-blue-600 cursor-pointer"
                data-testid="select-all-checkbox"
              />
            </th>
            <th className="px-4 py-4 text-left text-xs font-semibold text-slate-600 uppercase w-12">
              {i18n.language === 'ar' ? 'صورة' : 'Image'}
            </th>
            <th className="px-4 py-4 text-left text-xs font-semibold text-slate-600 uppercase">
              {i18n.language === 'ar' ? 'السؤال' : 'Question'}
            </th>
            <th className="px-4 py-4 text-left text-xs font-semibold text-slate-600 uppercase">
              {i18n.language === 'ar' ? 'الفئة' : 'Category'}
            </th>
            <th className="px-4 py-4 text-left text-xs font-semibold text-slate-600 uppercase">
              {i18n.language === 'ar' ? 'الإجابة الصحيحة' : 'Correct Answer'}
            </th>
            <th className="px-4 py-4 text-right text-xs font-semibold text-slate-600 uppercase">
              {i18n.language === 'ar' ? 'إجراءات' : 'Actions'}
            </th>
          </tr>
        </thead>
        <tbody className="divide-y divide-slate-200">
          {questions.map((q, index) => {
            const hasImage = q.image_url && 
              q.image_url !== 'None' && 
              q.image_url !== '' && 
              q.image_url !== 'undefined' &&
              q.image_url !== 'null' &&
              !q.image_url.includes('undefined') &&
              (q.image_url.startsWith('http') || q.image_url.startsWith('/'));
            const isSelected = selectedIds.includes(q.id);
            return (
            <tr key={`${q.id}-${currentLang}`} className={`hover:bg-slate-50 ${isSelected ? 'bg-blue-50' : ''}`}>
              <td className="px-3 py-4">
                <input
                  type="checkbox"
                  checked={isSelected}
                  onChange={() => toggleSelect(q.id)}
                  className="w-4 h-4 rounded border-slate-300 text-blue-600 cursor-pointer"
                  data-testid={`select-question-${q.id}`}
                />
              </td>
              <td className="px-4 py-4">
                {hasImage ? (
                  <div className="relative group">
                    <img 
                      src={q.image_url?.startsWith('http') ? q.image_url : `${q.image_url}?v=3`} 
                      alt="" 
                      className="w-10 h-10 object-cover rounded border border-green-300"
                    />
                    <div className="absolute -top-1 -right-1 w-4 h-4 bg-green-500 rounded-full flex items-center justify-center">
                      <span className="text-white text-xs">✓</span>
                    </div>
                  </div>
                ) : (
                  <div className="relative">
                    <div className="w-10 h-10 bg-red-50 rounded border-2 border-dashed border-red-300 flex items-center justify-center">
                      <ImageIcon className="w-4 h-4 text-red-400" />
                    </div>
                    <div className="absolute -top-1 -right-1 w-4 h-4 bg-red-500 rounded-full flex items-center justify-center animate-pulse">
                      <span className="text-white text-xs">!</span>
                    </div>
                  </div>
                )}
                {/* Video indicator: shows when the question has an attached
                    video so the admin can see at a glance that a video is
                    saved (otherwise the row looked "empty" and admins thought
                    the upload was lost). */}
                {q.video_url && q.video_url !== '' && q.video_url !== 'None' && (
                  <div className="mt-1 inline-flex items-center gap-1 px-1.5 py-0.5 rounded bg-emerald-100 text-emerald-700 text-[10px] font-semibold" title={q.video_url}>
                    🎬 Video
                  </div>
                )}
              </td>
              <td className="px-4 py-4">
                <div className="text-sm text-slate-900 line-clamp-2">
                  {getQuestionText(q.question)}
                </div>
              </td>
              <td className="px-4 py-4">
                <span className={`px-3 py-1 ${getCategoryColor(q.category)} text-white rounded-full text-xs font-medium`}>
                  {getCategoryLabel(q.category)}
                </span>
              </td>
              <td className="px-4 py-4 text-sm text-slate-600">
                {String.fromCharCode(65 + (q.correct_answer || q.correctAnswer || 0))}
              </td>
              <td className="px-4 py-4">
                <div className="flex items-center justify-end gap-2">
                  <Button variant="ghost" size="sm" onClick={() => copyQuestionSv(q)} className="hover:bg-slate-100" title="Kopiera fråga / نسخ السؤال" data-testid={`copy-question-${q.id}`}>
                    <Copy className="w-4 h-4 text-slate-600" />
                  </Button>
                  {hasImage ? (
                    <Button variant="ghost" size="sm" onClick={() => downloadQuestionImage(q)} className="hover:bg-emerald-50" title="Ladda ner bild / تحميل الصورة" data-testid={`download-image-${q.id}`}>
                      <Download className="w-4 h-4 text-emerald-600" />
                    </Button>
                  ) : <span className="inline-block w-9" aria-hidden />}
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => onEdit(q)}
                    className="hover:bg-blue-50"
                  >
                    <Edit className="w-4 h-4 text-blue-600" />
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => onDelete(q.id)}
                    className="hover:bg-red-50"
                  >
                    <Trash2 className="w-4 h-4 text-red-600" />
                  </Button>
                </div>
              </td>
            </tr>
          )})}
        </tbody>
      </table>
    </div>
  </div>
);
};

// Lessons Table Component
const LessonsTable = ({ lessons, onDelete, onEdit, i18n, getCategoryLabel, getCategoryColor }) => (
  <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
    <div className="overflow-x-auto">
      <table className="w-full">
        <thead className="bg-slate-50 border-b border-slate-200">
          <tr>
            <th className="px-6 py-4 text-left text-xs font-semibold text-slate-600 uppercase">
              {i18n.language === 'ar' ? 'العنوان' : 'Title'}
            </th>
            <th className="px-6 py-4 text-left text-xs font-semibold text-slate-600 uppercase">
              {i18n.language === 'ar' ? 'الفئة' : 'Category'}
            </th>
            <th className="px-6 py-4 text-right text-xs font-semibold text-slate-600 uppercase">
              {i18n.language === 'ar' ? 'إجراءات' : 'Actions'}
            </th>
          </tr>
        </thead>
        <tbody className="divide-y divide-slate-200">
          {lessons.map((l) => (
            <tr key={l.id} className="hover:bg-slate-50">
              <td className="px-6 py-4">
                <div className="text-sm font-medium text-slate-900">
                  {l.title?.[i18n.language] || l.title?.en || 'N/A'}
                </div>
              </td>
              <td className="px-6 py-4">
                <span className={`px-3 py-1 ${getCategoryColor(l.category)} text-white rounded-full text-xs font-medium`}>
                  {getCategoryLabel(l.category)}
                </span>
              </td>
              <td className="px-6 py-4">
                <div className="flex items-center justify-end gap-2">
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => onEdit(l)}
                    className="hover:bg-blue-50"
                  >
                    <Edit className="w-4 h-4 text-blue-600" />
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => onDelete(l.id)}
                    className="hover:bg-red-50"
                  >
                    <Trash2 className="w-4 h-4 text-red-600" />
                  </Button>
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  </div>
);

// Videos Table Component
// Traffic Signs Table Component
const TrafficSignsTable = ({ signs, onDelete, onEdit, i18n, getCategoryLabel, getCategoryColor }) => (
  <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
    <div className="overflow-x-auto">
      <table className="w-full">
        <thead className="bg-slate-50 border-b border-slate-200">
          <tr>
            <th className="px-6 py-4 text-left text-xs font-semibold text-slate-600 uppercase">
              {i18n.language === 'ar' ? 'الصورة' : 'Image'}
            </th>
            <th className="px-6 py-4 text-left text-xs font-semibold text-slate-600 uppercase">
              {i18n.language === 'ar' ? 'الاسم' : 'Name'}
            </th>
            <th className="px-6 py-4 text-left text-xs font-semibold text-slate-600 uppercase">
              {i18n.language === 'ar' ? 'الفئة' : 'Category'}
            </th>
            <th className="px-6 py-4 text-right text-xs font-semibold text-slate-600 uppercase">
              {i18n.language === 'ar' ? 'إجراءات' : 'Actions'}
            </th>
          </tr>
        </thead>
        <tbody className="divide-y divide-slate-200">
          {signs.length === 0 ? (
            <tr>
              <td colSpan="4" className="px-6 py-8 text-center text-slate-500">
                {i18n.language === 'ar' ? 'لا توجد إشارات' : 'No signs found'}
              </td>
            </tr>
          ) : signs.map((s) => (
            <tr key={s.id} className="hover:bg-slate-50">
              <td className="px-6 py-4">
                {s.image_url ? (
                  <img 
                    src={s.image_url} 
                    alt={s.name?.[i18n.language] || s.name?.sv || 'Sign'} 
                    className="w-12 h-12 object-contain rounded"
                  />
                ) : (
                  <div className="w-12 h-12 bg-slate-100 rounded flex items-center justify-center">
                    <AlertTriangle className="w-6 h-6 text-slate-400" />
                  </div>
                )}
              </td>
              <td className="px-6 py-4">
                <div className="text-sm font-medium text-slate-900">
                  {s.name?.[i18n.language] || s.name?.sv || s.name?.en || 'N/A'}
                </div>
              </td>
              <td className="px-6 py-4">
                <span className={`px-3 py-1 ${getCategoryColor(s.category)} text-white rounded-full text-xs font-medium`}>
                  {getCategoryLabel(s.category)}
                </span>
              </td>
              <td className="px-6 py-4">
                <div className="flex items-center justify-end gap-2">
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => onEdit(s)}
                    className="hover:bg-blue-50"
                  >
                    <Edit className="w-4 h-4 text-blue-600" />
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => onDelete(s.id)}
                    className="hover:bg-red-50"
                  >
                    <Trash2 className="w-4 h-4 text-red-600" />
                  </Button>
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  </div>
);

// Quick Tips Table Component
const QuickTipsTable = ({ tips, onDelete, onEdit, i18n, getCategoryLabel, getCategoryColor }) => {
  const tipCategories = {
    speed: { ar: 'السرعة', sv: 'Hastighet', en: 'Speed', color: 'bg-blue-500' },
    safety: { ar: 'السلامة', sv: 'Säkerhet', en: 'Safety', tr: 'Güvenlik', fa: 'ایمنی', so: 'Badbaado', pl: 'Bezpieczeństwo', es: 'Seguridad', de: 'Sicherheit', color: 'bg-green-500' },
    rules: { ar: 'القواعد', sv: 'Regler', en: 'Rules', tr: 'Kurallar', fa: 'قوانین', so: 'Xeerarka', pl: 'Przepisy', es: 'Reglas', de: 'Regeln', color: 'bg-orange-500' },
    signs: { ar: 'الإشارات', sv: 'Skyltar', en: 'Signs', tr: 'İşaretler', fa: 'علائم', so: 'Calaamadaha', pl: 'Znaki', es: 'Señales', de: 'Schilder', color: 'bg-yellow-500' },
    weather: { ar: 'الطقس', sv: 'Väder', en: 'Weather', tr: 'Hava', fa: 'آب و هوا', so: 'Cimilada', pl: 'Pogoda', es: 'Clima', de: 'Wetter', color: 'bg-slate-500' },
    parking: { ar: 'الوقوف', sv: 'Parkering', en: 'Parking', tr: 'Park', fa: 'پارکینگ', so: 'Baarkinka', pl: 'Parkowanie', es: 'Aparcamiento', de: 'Parken', color: 'bg-emerald-500' },
    exam: { ar: 'الاختبار', sv: 'Prov', en: 'Exam', tr: 'Sınav', fa: 'آزمون', so: 'Imtixaanka', pl: 'Egzamin', es: 'Examen', de: 'Prüfung', color: 'bg-lime-500' },
    vehicle: { ar: 'السيارة', sv: 'Fordon', en: 'Vehicle', tr: 'Araç', fa: 'خودرو', so: 'Gaariga', pl: 'Pojazd', es: 'Vehículo', de: 'Fahrzeug', color: 'bg-gray-500' },
    eco: { ar: 'بيئي', sv: 'Miljö', en: 'Eco', tr: 'Çevre', fa: 'زیست محیطی', so: 'Deegaanka', pl: 'Ekologia', es: 'Ecología', de: 'Umwelt', color: 'bg-green-600' },
    general: { ar: 'عام', sv: 'Allmänt', en: 'General', tr: 'Genel', fa: 'عمومی', so: 'Guud', pl: 'Ogólne', es: 'General', de: 'Allgemein', color: 'bg-purple-500' },
  };

  const getTipCategoryLabel = (cat) => {
    const category = tipCategories[cat] || tipCategories.general;
    return category[i18n.language] || category.en;
  };

  const getTipCategoryColor = (cat) => {
    return tipCategories[cat]?.color || 'bg-purple-500';
  };

  return (
    <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead className="bg-slate-50 border-b border-slate-200">
            <tr>
              <th className="px-6 py-4 text-left text-xs font-semibold text-slate-600 uppercase">
                {i18n.language === 'ar' ? 'العنوان' : 'Title'}
              </th>
              <th className="px-6 py-4 text-left text-xs font-semibold text-slate-600 uppercase">
                {i18n.language === 'ar' ? 'الفئة' : 'Category'}
              </th>
              <th className="px-6 py-4 text-left text-xs font-semibold text-slate-600 uppercase">
                {i18n.language === 'ar' ? 'المحتوى' : 'Content'}
              </th>
              <th className="px-6 py-4 text-right text-xs font-semibold text-slate-600 uppercase">
                {i18n.language === 'ar' ? 'إجراءات' : 'Actions'}
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-200">
            {tips.length === 0 ? (
              <tr>
                <td colSpan="4" className="px-6 py-8 text-center text-slate-500">
                  {i18n.language === 'ar' ? 'لا توجد نصائح' : 'No tips found'}
                </td>
              </tr>
            ) : tips.map((t) => (
              <tr key={t.id} className="hover:bg-slate-50">
                <td className="px-6 py-4">
                  <div className="text-sm font-medium text-slate-900">
                    {t.title?.[i18n.language] || t.title?.sv || t.title?.en || 'N/A'}
                  </div>
                </td>
                <td className="px-6 py-4">
                  <span className={`px-3 py-1 ${getTipCategoryColor(t.category)} text-white rounded-full text-xs font-medium`}>
                    {getTipCategoryLabel(t.category)}
                  </span>
                </td>
                <td className="px-6 py-4">
                  <div className="text-sm text-slate-600 max-w-md truncate">
                    {(t.content?.[i18n.language] || t.content?.sv || t.content?.en || '').substring(0, 100)}...
                  </div>
                </td>
                <td className="px-6 py-4">
                  <div className="flex items-center justify-end gap-2">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => onEdit(t)}
                      className="hover:bg-blue-50"
                    >
                      <Edit className="w-4 h-4 text-blue-600" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => onDelete(t.id)}
                      className="hover:bg-red-50"
                    >
                      <Trash2 className="w-4 h-4 text-red-600" />
                    </Button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

// Videos Table Component — admin overview with thumbnail, title, category, actions
// Auto-derives a YouTube thumbnail when no thumbnail_url was uploaded, so the
// table never shows blank placeholders for YouTube videos.
const getAdminThumb = (v) => {
  if (v?.thumbnail_url && v.thumbnail_url.trim()) return v.thumbnail_url;
  const url = v?.video_url || '';
  const m = url.match(/(?:youtu\.be\/|youtube\.com\/(?:watch\?v=|embed\/|shorts\/))([A-Za-z0-9_-]{6,})/);
  if (m) return `https://img.youtube.com/vi/${m[1]}/hqdefault.jpg`;
  return null;
};

const VideosTable = ({ videos, onDelete, onEdit, i18n }) => (
  <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
    <div className="overflow-x-auto">
      <table className="w-full">
        <thead className="bg-slate-50 border-b border-slate-200">
          <tr>
            <th className="px-6 py-4 text-left text-xs font-semibold text-slate-600 uppercase">
              {i18n.language === 'ar' ? 'صورة مصغرة' : 'Thumbnail'}
            </th>
            <th className="px-6 py-4 text-left text-xs font-semibold text-slate-600 uppercase">
              {i18n.language === 'ar' ? 'العنوان' : 'Title'}
            </th>
            <th className="px-6 py-4 text-left text-xs font-semibold text-slate-600 uppercase">
              {i18n.language === 'ar' ? 'الفئة' : 'Category'}
            </th>
            <th className="px-6 py-4 text-left text-xs font-semibold text-slate-600 uppercase">
              {i18n.language === 'ar' ? 'المدة' : 'Duration'}
            </th>
            <th className="px-6 py-4 text-right text-xs font-semibold text-slate-600 uppercase">
              {i18n.language === 'ar' ? 'إجراءات' : 'Actions'}
            </th>
          </tr>
        </thead>
        <tbody className="divide-y divide-slate-200">
          {videos.length === 0 ? (
            <tr>
              <td colSpan="5" className="px-6 py-8 text-center text-slate-500">
                {i18n.language === 'ar' ? 'لا توجد فيديوهات' : 'Inga videor'}
              </td>
            </tr>
          ) : videos.map((v) => {
            const adminThumb = getAdminThumb(v);
            return (
            <tr key={v.id} className="hover:bg-slate-50" data-testid={`video-row-${v.id}`}>
              <td className="px-6 py-4">
                {adminThumb ? (
                  <img
                    src={adminThumb}
                    alt={v.title?.[i18n.language] || v.title?.sv || 'Video'}
                    className="w-20 h-12 object-cover rounded"
                    onError={(e) => { e.target.style.display = 'none'; }}
                  />
                ) : (
                  <div className="w-20 h-12 bg-slate-100 rounded flex items-center justify-center">
                    <PlayCircle className="w-6 h-6 text-slate-400" />
                  </div>
                )}
              </td>
              <td className="px-6 py-4">
                <div className="text-sm font-medium text-slate-900 max-w-xs truncate">
                  {v.title?.[i18n.language] || v.title?.sv || v.title?.en || 'N/A'}
                </div>
              </td>
              <td className="px-6 py-4">
                <span className="px-3 py-1 bg-purple-100 text-purple-700 rounded-full text-xs font-medium">
                  {v.category || 'basics'}
                </span>
              </td>
              <td className="px-6 py-4 text-sm text-slate-600">
                {v.duration ? `${v.duration}s` : '-'}
              </td>
              <td className="px-6 py-4 text-right">
                <div className="flex items-center justify-end gap-2">
                  <Button size="sm" variant="ghost" onClick={() => onEdit(v)} data-testid={`edit-video-${v.id}`}>
                    <Edit className="w-4 h-4" />
                  </Button>
                  <Button size="sm" variant="ghost" className="text-red-600 hover:bg-red-50" onClick={() => onDelete(v.id)} data-testid={`delete-video-${v.id}`}>
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              </td>
            </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  </div>
);

const copyQuestionSv = async (q) => {
  const opts = q.options?.sv || [];
  const ci = q.correct_answer ?? q.correctAnswer ?? 0;
  const L = (i) => String.fromCharCode(65 + i);
  const text = [
    'Fråga:', q.question?.sv || '',
    ...opts.map((o, i) => `${L(i)}. ${o}`),
    `Rätt svar: ${L(ci)} ✅`,
    'Förklaring:', q.explanation?.sv || '',
  ].join('\n').trim();
  try {
    await navigator.clipboard.writeText(text);
  } catch {
    const ta = document.createElement('textarea');
    ta.value = text; document.body.appendChild(ta); ta.select(); document.execCommand('copy'); ta.remove();
  }
  toast.success('Frågan kopierad / تم نسخ السؤال');
};

const downloadQuestionImage = async (q) => {
  try {
    const res = await fetch(q.image_url);
    if (!res.ok) throw new Error('missing');
    const blob = await res.blob();
    const ext = (blob.type.split('/')[1] || 'jpg').replace('jpeg', 'jpg');
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = `fraga-${q.id.slice(0, 8)}.${ext}`;
    a.click();
    URL.revokeObjectURL(a.href);
  } catch {
    toast.error('Bilden finns inte på servern / الصورة غير موجودة على الخادم');
  }
};

export default ContentManagement;
